import {useEffect, useState} from 'react'
import _ from 'lodash'
// Utils
import {keenClient} from '../utils/analytics'
import {normalizeUrlWithProtocol} from '../utils/string-helpers'
// Types
import type {SponsorshipAd} from '../types/ballot'
// Styles
import styles from './sponsor-display.module.scss'
// Sample ballot sponsorship ad:
// {
//   "_id": "628e8d2ff32c733b7aa1921a",
//   "enable": false,
//   "ad_name": "Sponsorship Ad 01",
//   "ad_link": "https://ucarecdn.com/58536a57-7a59-48c5-a072-15e6645800d1/",
//   "ad_link_url": "https://vote.henketime.com",
//   "ad_text": "Presented by:",
//   "include_votepage_logo": true,
//   "ad_value": "$100",
//   "updated_date_time": "2022-05-25T20:10:23.118Z"
// }
interface SponsorDisplayProps {
  ballotId: string
  sponsorshipAds: SponsorshipAd[]
  styleModifiers?: string[]
}

const SponsorDisplay = ({
  ballotId,
  sponsorshipAds,
  styleModifiers,
}: SponsorDisplayProps) => {
  if (_.isEmpty(sponsorshipAds)) return null

  const [tierOneSponsorshipAds, setTierOneSponsorshipAds] = useState<SponsorshipAd[]>([])
  const [tierTwoSponsorshipAds, setTierTwoSponsorshipAds] = useState<SponsorshipAd[]>([])

  const [className, setClassName] = useState(styles['sponsor-display'])

  const [adTextIsToTheLeft, setAdTextIsToTheLeft] = useState(true)

  const handleAdClick = (ad: SponsorshipAd) => {
    keenClient.recordEvent('Sponsorship Ad Click', {
      event_type: 'Sponsorship Ad Click',
      ballot_id: ballotId,
      ad_id: ad?._id,
      ad_name: ad?.ad_name,
      website_link: ad?.ad_link_url,
    })
  }

  const calculateSponsorAdDom = (sponsorshipAd: SponsorshipAd, index: number | null = null) => {
    const adLinkUrl = normalizeUrlWithProtocol(sponsorshipAd?.ad_link_url, 'https')
    let sponsorAdDom = null
    if (adLinkUrl) {
      sponsorAdDom = (
        <a
          key={index}
          href={adLinkUrl}
          target="_blank"
          className={styles['__ad-link']}
          rel="noreferrer"
        >
          <img
            src={sponsorshipAd?.ad_link}
            // alt="sponsor image"
            onClick={() => handleAdClick(sponsorshipAd)}
            className={styles['__ad-image']}
          />
        </a>
      )
    } else {
      sponsorAdDom = (
        <div
          key={index}
          className={styles['__ad-link']}
        >
          <img
            src={sponsorshipAd?.ad_link}
            // alt="sponsor image"
            className={styles['__ad-image']}
          />
        </div>
      )
    }
    return sponsorAdDom
  }

  useEffect(() => {
    let mounted = true
    if (mounted) {
      const newTierOneSponsorshipAds = _.filter(sponsorshipAds, sponsorshipAd => sponsorshipAd.tier === 'tier-1')
      setTierOneSponsorshipAds(newTierOneSponsorshipAds)

      const newTierTwoSponsorshipAds = _.filter(sponsorshipAds, sponsorshipAd => sponsorshipAd.tier === 'tier-2')
      setTierTwoSponsorshipAds(newTierTwoSponsorshipAds)

      const newAdTextIsToTheLeft = (_.includes(styleModifiers, '--ad-text-is-to-the-left'))
      setAdTextIsToTheLeft(newAdTextIsToTheLeft)

      let newClassName = styles['sponsor-display']
      if ((styleModifiers?.length ?? 0) > 0)
        newClassName += ` ${_.map(styleModifiers, modifierName => styles[modifierName]).join(' ')}`
      if (newTierOneSponsorshipAds.length === 1)
        newClassName += ` ${styles['--tier-one-is-solo']}`
      if (newTierTwoSponsorshipAds.length === 1)
        newClassName += ` ${styles['--tier-two-is-solo']}`
      setClassName(newClassName)
    }
    return () => {
      mounted = false
    }
  }, [])

  return <>
    <div className={className}>
      {(tierOneSponsorshipAds?.length > 0) &&
        <div className={styles['__tier-one-row']}>
          {adTextIsToTheLeft &&
            <p className={styles['__ad-text']}>{_.first(tierOneSponsorshipAds)?.ad_text || 'Sponsored by:'}</p>
          }
          {!adTextIsToTheLeft &&
            <div className={styles['__ad-text-container']}>
              <p className={styles['__ad-text']}>{_.first(tierOneSponsorshipAds)?.ad_text || 'Sponsored by:'}</p>
            </div>
          }
          {(tierOneSponsorshipAds?.length === 1)
            ? calculateSponsorAdDom(tierOneSponsorshipAds[0])
            : _.map(tierOneSponsorshipAds, (tierOneSponsorshipAd, index) => {
              return calculateSponsorAdDom(tierOneSponsorshipAd, index)
            })
          }
        </div>
      }
      {(tierTwoSponsorshipAds?.length > 0) &&
        <div className={styles['__tier-two-row']}>
          {(tierOneSponsorshipAds?.length === 0) && <>
            {adTextIsToTheLeft &&
              <p className={styles['__ad-text']}>{_.first(tierTwoSponsorshipAds)?.ad_text || 'Sponsored by:'}</p>
            }
            {!adTextIsToTheLeft &&
              <div className={styles['__ad-text-container']}>
                <p className={styles['__ad-text']}>{_.first(tierTwoSponsorshipAds)?.ad_text || 'Sponsored by:'}</p>
              </div>
            }
          </>}
          {(tierTwoSponsorshipAds?.length === 1)
            ? calculateSponsorAdDom(tierTwoSponsorshipAds[0])
            : _.map(tierTwoSponsorshipAds, (tierTwoSponsorshipAd, index) => {
              return calculateSponsorAdDom(tierTwoSponsorshipAd, index)
            })
          }
        </div>
      }
    </div>
  </>
}

export default SponsorDisplay
